import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import { PaymentsSuccessCodes } from '@app/translations';
import { FormID } from '@finxone-platform/form-action';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  ActionButtonOutcomesType,
  AlertTypes,
  BaseWidgetProperties,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { catchError, tap } from 'rxjs';
import { UpdateFormDataAction } from '../../../actions/form-submission.action';
import { CtaButtonSignalService } from '../../../services/cta-button-signal-service/cta-button-signal.service';
import { SavingsAccountService } from '../../../services/savings-account-service/savings-account.service';
import { WorkflowFormsService } from '../../../services/workflow-forms/workflow-forms.service';
import { ProjectSettingsState } from '../../../state/project-settings.state';
import { SavingsAccountState } from '../../../state/savings-account.state';
import { findAndShowAlertFromCode } from '../../alert-code-utils/alert-code.utils';
import { redirectToPage } from '../cta-button-actions.utils';
import { handleRedirection } from '../cta-redirection.utils';
import { GetSavingAccount, UpdateActiveSavingsAccountId } from '../../../actions/savings-account.action';
import { getPageSizeByProvider } from '../../query-params.utils';

export function initiateWithdrawFromSavingsAccount(
  widgetProperties: BaseWidgetProperties,
  injector: Injector,
) {
  const ctaButtonSignalService = injector.get(CtaButtonSignalService);
  const workflowFormsService = injector.get(WorkflowFormsService);
  const savingsAccountService = injector.get(SavingsAccountService);
  const store = injector.get(Store);
  const alertHandlerService = injector.get(AlertHandlerService);
  const router = injector.get(Router);
  const currentAccountId = store.selectSnapshot(SavingsAccountState.getCurrentAccountId);
  // get all forms values in one object
  const allPageFormValues = workflowFormsService.getAllFormValuesFlattened();
  const currentPageFormValues = allPageFormValues[FormID.WITHDRAWAL_FORM];
  const activeSavingsAccountDetails = store.selectSnapshot(SavingsAccountState.getCurrentAccount);
  const ctaAction = widgetProperties.buttonActionType as string;

  if (!currentPageFormValues) {
    console.error('Withdrawal form data not found');
    alertHandlerService.showAlertFn(AlertTypes.ERROR, 'Withdrawal form data not found');
    return;
  }

  const amount = Number(currentPageFormValues['amount']);
  const activeAvailableBalance = Number(activeSavingsAccountDetails?.availableBalance);

  // 1. Check for missing or invalid numerical inputs
  if (Number.isNaN(amount)) {
    return alertHandlerService.showAlertFn(AlertTypes.WARNING, 'Withdrawal amount not found');
  }

  // 2. Check for zero amount
  if (amount === 0) {
    return alertHandlerService.showAlertFn(AlertTypes.WARNING, 'Please enter valid amount');
  }

  // 3. Check for insufficient funds
  if (amount > activeAvailableBalance) {
    return alertHandlerService.showAlertFn(
      AlertTypes.WARNING,
      'Entered amount is more than available balance',
    );
  }

  // 4. Full balance withdrawal - if configured, interrupt the standard flow and
  // redirect into the account closure journey instead of submitting the withdrawal
  const fullBalanceOutcomeConfigured =
    widgetProperties?.actionNavigationConfigurations?.[ActionButtonOutcomesType.ALTERNATE_1];

  const fullBalanceWithdrawalValidation = store.selectSnapshot(ProjectSettingsState.getProjectSettings)
    ?.projectConfig?.account_service?.fullBalanceWithdrawalValidation;
  const isClosureEnabled = activeSavingsAccountDetails?.isClosureEnabled;
  if (
    fullBalanceOutcomeConfigured &&
    fullBalanceWithdrawalValidation &&
    isClosureEnabled &&
    amount === activeAvailableBalance
  ) {
    store.dispatch(new UpdateActiveSavingsAccountId(activeSavingsAccountDetails?.accountId));
    return handleRedirection(widgetProperties, injector, ActionButtonOutcomesType.ALTERNATE_1, true);
  }

  // disable button on click
  ctaButtonSignalService.setSignal({ [ctaAction]: false });

  const isNoticeAccount = activeSavingsAccountDetails?.accountType === 'notice';

  savingsAccountService
    .withdrawFromSavingsAccount(currentAccountId, {
      amount: amount,
      description: currentPageFormValues?.['description']
        ? String(currentPageFormValues?.['description'])
        : undefined,
    })
    .pipe(
      tap(() => {
        ctaButtonSignalService.setSignal({ [ctaAction]: true });
        const successCode = isNoticeAccount
          ? PaymentsSuccessCodes.SUBMIT_WITHDRAWAL_NOTICE
          : PaymentsSuccessCodes.WITHDRAW_SUCCESS;
        findAndShowAlertFromCode(store, router, alertHandlerService, [successCode]);
        // store this withdrawal status in form action state to be used by the builders in next step
        store.dispatch(new UpdateFormDataAction({ formData: { withdrawalStatus: 'completed' } }));
        if (!widgetProperties?.actionNavigationConfigurations) {
          redirectToPage(router, widgetProperties, injector);
        } else handleRedirection(widgetProperties, injector, ActionButtonOutcomesType.SUCCESS, true);

        // try refresh the account details after withdrawal in case of triple access, so that the account details are updated in the state
        const pageSize = getPageSizeByProvider(store);
        store.dispatch(new GetSavingAccount(1, pageSize));
      }),
      catchError((err) => {
        ctaButtonSignalService.setSignal({ [ctaAction]: true });
        console.error(err);
        // store this withdrawal status in form action state to be used by the builders in next step
        store.dispatch(new UpdateFormDataAction({ formData: { withdrawalStatus: 'failed' } }));
        if (widgetProperties?.actionNavigationConfigurations)
          handleRedirection(widgetProperties, injector, ActionButtonOutcomesType.FAILURE, true);
        else redirectToPage(router, widgetProperties, injector);
        throw err;
      }),
    )
    .subscribe();
}
